import {Component, Input, OnInit} from '@angular/core';
import {AccountRole, TextUtils, AccountMetaDto} from 'common';
import {MatDialog} from '@angular/material/dialog';
import {AccountAdminDto} from '../../dtos/accountAdmin.dto';
import {AccountMetaKeys} from '../../models/accountMetaKeys';
import {AccountAdminService} from '../../services/api/account-admin.service';
import {BaseComponent} from '../base.component';
import {DialogConfirmComponent} from '../dialogs/dialog-confirm';
import {v4 as uuidv4} from 'uuid';
@Component({
    selector: 'app-account-info',
    templateUrl: './account-info.component.html',
    styleUrls: ['./account-info.component.scss']
})
export class AccountInfoComponent extends BaseComponent implements OnInit {

    constructor(private accountsService: AccountAdminService, private dialog: MatDialog) {
        super();
    }

    public get salesmanPlaceholder(): string {
        return 'Enter salesman name..';
    }

    public get saveSalesmanVisible(): boolean {
        return this.salesman !== this.account.getMetaString(AccountMetaKeys.SALESMAN);
    }

    @Input()
    public account: AccountAdminDto;

    public salesman: string;

    ngOnInit(): void {
        this.salesman = this.account.getMetaString(AccountMetaKeys.SALESMAN);
    }

    public formatSpecialty(): string {
        if (TextUtils.isEmpty(this.account.specialty)) {
            return '-';
        }

        return this.account.specialty.replace('_', ' ').toLowerCase();
    }

    public formatCountryName(): string {
        let name = '-';
        if (this.account.country) {
            // @ts-ignore
            const regionNamesInEnglish = new Intl.DisplayNames(['EN'], {type: 'region'});
            name = regionNamesInEnglish.of(this.account.country);
        }
        return name;
    }

    public accountIsPatient(role: AccountRole): boolean {
        return role === AccountRole.PATIENT;
    }

    /**
     * An account is deleted when the user asked for its deletion, which adds the deleted_on meta.
     * The deleted database column is not used by that flow, so it cannot be trusted here.
     */
    public get accountIsDeleted(): boolean {
        return this.account.getMeta(AccountMetaKeys.DELETED_ON) !== undefined;
    }

    public get deletedOnDate(): Date | undefined {
        const metaValue = this.account.getMetaString(AccountMetaKeys.DELETED_ON);

        if (TextUtils.isEmpty(metaValue)) {
            return undefined;
        }

        return new Date(parseFloat(metaValue));
    }

    public get canRestoreAccount(): boolean {
        return this.userAccount?.accountRoleType === AccountRole.ADMIN;
    }

    public onRestoreAccountPressed(): void {
        this.dialog.open(DialogConfirmComponent, {
            data: {
                title: 'Restore this account?',
                warning: 'This gives the user access to their account again.',
                message: `The deletion mark will be removed from ${this.account.email} and the user will be able to log in.\n\n`
                    + 'Subscriptions and any data already removed are not restored, and the deletion emails that were '
                    + 'sent cannot be recalled.',
                confirmText: 'Restore account'
            }
        }).afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.restoreAccount();
            }
        });
    }

    private restoreAccount(): void {
        const deletedOnMeta = this.account.getMeta(AccountMetaKeys.DELETED_ON);

        if (!deletedOnMeta) {
            return;
        }

        this.accountsService.deleteAccountMeta(this.account.code, deletedOnMeta.code)
            .subscribe(() => {
                this.accountsService.get(this.account.code)
                    .subscribe(account => {
                        this.account = account;
                        this.notification.info('Account restored.');
                    }, error => {
                        this.notification.info(`Account restored, but reloading it failed: ${error.statusText} (${error.status})`);
                    });
            }, error => {
                this.notification.info(`Failed to restore account: ${error.statusText} (${error.status})`);
            });
    }

    public saveSalesman(): void {
        const clone = this.account.clone();
        let newMeta = clone.getMeta(AccountMetaKeys.SALESMAN);
        if (!newMeta) {
            newMeta = new AccountMetaDto();
            newMeta.Code = uuidv4();
            newMeta.metaKey = AccountMetaKeys.SALESMAN;
            clone.metaFields.push(newMeta);
        }

        newMeta.metaValue = this.salesman;

        this.accountsService.updateAccountMetas(clone)
            .subscribe(response => {
                this.account = response;
                this.notification.info(`Saved Salesman!`);
            }, error => {
                this.notification.info(`Failed to save Salesman: ${error.statusText} (${error.status})`);
            });
    }
}
